import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { friendlyError } from '../utils/friendlyError';

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('user');
    if (!storedUser) { setLoading(false); return; }
    let parsed = null;
    try {
      parsed = JSON.parse(storedUser);
      setUser(parsed);
    } catch {
      localStorage.removeItem('user');
      setLoading(false);
      return;
    }

    // Wave 42.08 — Refetch the user record from the DB on every mount so
    // stale localStorage never causes wrong audience routing. If the fetch
    // fails (network/backend down) we just keep the cached copy.
    if (parsed?.id) {
      axios.get(`${API}/users/${encodeURIComponent(parsed.id)}`)
        .then((r) => {
          const fresh = r?.data?.user;
          if (fresh && fresh.id === parsed.id) {
            setUser(fresh);
            localStorage.setItem('user', JSON.stringify(fresh));
          }
        })
        .catch(() => { /* keep stale copy on network error */ })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, role = null) => {
    try {
      const endpoint = role === 'admin' ? `${API}/auth/admin-login` : `${API}/auth/login`;
      const response = await axios.post(
        endpoint,
        { email, password, role },
        { timeout: 20000 },  // 20s ceiling — surfaces backend outages instead of hanging forever
      );
      
      if (response.data.success) {
        const userData = response.data.user;
        const token = response.data.token;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        if (token) {
          localStorage.setItem('token', token);
        }
        return { success: true, user: userData, token };
      }
      return { success: false, message: 'Login failed' };
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        return {
          success: false,
          message: 'Login is taking too long. The server may be waking up — please try again in 10 seconds.',
        };
      }
      return {
        success: false,
        message: friendlyError(error, { fallback: 'Login failed' }),
      };
    }
  };

  const register = async (data) => {
    try {
      const response = await axios.post(`${API}/auth/register`, data);
      
      if (response.data.success) {
        const userData = response.data.user;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, user: userData };
      }
      return { success: false, message: 'Registration failed' };
    } catch (error) {
      return {
        success: false,
        message: friendlyError(error, { fallback: 'Registration failed' }),
      };
    }
  };

  const logout = () => {
    // Best-effort server-side revocation so the old token can't be
    // replayed after sign-out (fire-and-forget, never blocks the UI).
    try {
      const token = localStorage.getItem('token');
      if (token) {
        axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        }).catch(() => { /* ignore — token still gets cleared locally */ });
      }
    } catch (_) { /* ignore */ }
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const value = {
    user,
    setUser,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};